import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import {
    useCreateClientMutation,
    useUpdateClientMutation,
    useGetTiersQuery,
    useGetTradersQuery,
    useGetSectorsQuery,
    useGetCategoriesQuery,
    useGetSubCategoriesQuery,
} from "../../../redux/service/modules/admin";
import { useNotification } from "../../../context/NotificationContext";
import { FormField } from "../../../components/common/FormField";
import { ActionButtons } from "../../../components/common/ActionButton";
import CommonDialog from "../../../components/common/CommonDialog";
import {
    Autocomplete,
    Box,
    CircularProgress,
    TextField,
    Typography,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    FormControl,
    InputLabel,
    Chip,
} from "@mui/material";

const ClientDetailDialog = ({
    open,
    onClose,
    rowData,
    isEdit,
    title,
    ctaText,
}) => {
    const { control, handleSubmit, setValue, reset, setError } = useForm();
    const [updateClient, { isLoading }] = useUpdateClientMutation();
    const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
    const { data: tiers } = useGetTiersQuery({});
    const { data: traders } = useGetTradersQuery({});
    const { data: sectors } = useGetSectorsQuery({});
    const { data: categories } = useGetCategoriesQuery({});
    const { data: subCategories } = useGetSubCategoriesQuery({});

    const { showNotification } = useNotification();

    useEffect(() => {
        if (rowData) {
            Object.keys(rowData).forEach((key) => {
                if (key === "category_id") {
                    setValue("category", rowData[key]);
                } else if (key === "sub_category_id") {
                    setValue("sub_category", rowData[key]);
                } else if (key === "trader_id") {
                    setValue("trader", rowData[key]);
                } else if (key === "sector_id") {
                    setValue("sectors", rowData[key].map(Number));
                } else if (key === "tier_id") {
                    setValue("tier", rowData[key]);
                } else {
                    setValue(key, rowData[key]);
                }
            });
        }
    }, [rowData, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data) => {
        try {
            const formDataToSend = {};
            Object.keys(data).forEach((key) => {
                if (data[key] !== undefined) {
                    formDataToSend[key] = data[key];
                }
            });
            if (isEdit) {
                await updateClient({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Client Updated successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Update Client"
                            );
                        }
                    });
            } else {
                await createClient({ data: formDataToSend })
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            showNotification(
                                "success",
                                "Client Created successfully!",
                                ""
                            );
                            handleClose();
                        } else {
                            showNotification(
                                "error",
                                "An Error Occurred",
                                "Failed to Create Client"
                            );
                            handleClose();
                        }
                    })
                    .catch((error) => {
                        if (error.status === 400) {
                            setError("client_name", {
                                type: "manual",
                                message: error.data.client_name[0],
                            });
                        }
                    });
            }
        } catch (error) {
            console.error("Failed to update client:", error);
        }
    };

    return (
        <CommonDialog open={open} onClose={onClose} title={title}>
            <div className="xl:p-2 2xl:p-4 3xl:p-4 xl:pt-4 2xl:pt-4 3xl:pt-6 xl:ml-2 2xl:ml-4 3xl:ml-4">
                <div className="flex flex-col xl:gap-2 2xl:gap-4 3xl:gap-6">
                    <Controller
                        name="client_name"
                        control={control}
                        rules={{
                            required: "Client Name is required",
                        }}
                        render={({ field, fieldState }) => (
                            <FormField
                                label="Client Name"
                                {...field}
                                required
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />

                    <Controller
                        name="group_name"
                        control={control}
                        render={({ field }) => (
                            <FormField label="Group Name" {...field} />
                        )}
                    />

                    <Controller
                        name="tier"
                        control={control}
                        rules={{ required: "Tier is required" }}
                        render={({ field, fieldState }) => (
                            <FormField
                                label="Tier"
                                select
                                {...field}
                                required
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            >
                                <option value="" disabled selected>
                                    Select Tier
                                </option>
                                {tiers?.data?.map((tier) => (
                                    <option key={tier.id} value={tier.id}>
                                        {tier.tier_name}
                                    </option>
                                ))}
                            </FormField>
                        )}
                    />

                    <Controller
                        name="category"
                        control={control}
                        rules={{ required: "Category is required" }}
                        render={({ field, fieldState }) => (
                            <FormField
                                label="Category"
                                select
                                {...field}
                                required
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            >
                                <option value="" disabled selected>
                                    Select Category
                                </option>
                                {categories?.data?.map((category) => (
                                    <option
                                        key={category.id}
                                        value={category.id}
                                    >
                                        {category.category_name}
                                    </option>
                                ))}
                            </FormField>
                        )}
                    />

                    {/* <Controller
                        name="sub_category"
                        control={control}
                        rules={{ required: "Sub-Category is required" }}
                        render={({ field, fieldState }) => (
                            <FormField
                                label="Sub-Category"
                                select
                                {...field}
                                required
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            >
                                <option value="" disabled selected>
                                    Select Sub-Category
                                </option>
                                {subCategories?.data?.map((subCategory) => (
                                    <option
                                        key={subCategory.id}
                                        value={subCategory.id}
                                    >
                                        {subCategory.sub_category_name}
                                    </option>
                                ))}
                            </FormField>
                        )}
                    /> */}

                    <div className="space-y-2">
                        <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                            Sub Category
                        </label>
                        <Controller
                            name="sub_category"
                            control={control}
                            render={({ field, fieldState }) => (
                                <div className="relative">
                                    <Autocomplete
                                        multiple
                                        disableCloseOnSelect
                                        options={subCategories?.data || []}
                                        getOptionLabel={(option) =>
                                            option.sub_category_name
                                        }
                                        value={
                                            (subCategories?.data || []).filter(
                                                (subCategory) =>
                                                    field.value?.includes(
                                                        subCategory.id
                                                    )
                                            ) || []
                                        }
                                        onChange={(_, newValue) => {
                                            field.onChange(
                                                newValue.map((item) => item.id)
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                className="w-full xl:text-xs 2xl:text-sm 3xl:text-base"
                                                sx={{
                                                    "& .MuiOutlinedInput-root":
                                                        {
                                                            padding:
                                                                "0px 8px !important",
                                                            "& .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    border: "none",
                                                                },
                                                        },
                                                    "& .MuiInputBase-root": {
                                                        border: "1px solid rgb(229, 231, 235)",
                                                        borderRadius:
                                                            "0.375rem",
                                                        "@media (min-width: 1280px)":
                                                            {
                                                                borderWidth:
                                                                    "1px",
                                                                borderRadius:
                                                                    "0.375rem",
                                                            },
                                                        "@media (min-width: 1536px)":
                                                            {
                                                                borderWidth:
                                                                    "2px",
                                                                borderRadius:
                                                                    "0.5rem",
                                                            },
                                                        "@media (min-width: 1920px)":
                                                            {
                                                                borderWidth:
                                                                    "2px",
                                                                borderRadius:
                                                                    "0.5rem",
                                                            },
                                                        "&:hover": {
                                                            borderColor:
                                                                "rgb(239, 68, 68)",
                                                        },
                                                        "&.Mui-focused": {
                                                            borderColor:
                                                                "rgb(239, 68, 68)",
                                                            boxShadow:
                                                                "0 0 0 2px rgba(239, 68, 68, 0.2)",
                                                        },
                                                    },
                                                    "& .MuiAutocomplete-input":
                                                        {
                                                            padding:
                                                                "0.375rem 0.5rem !important",
                                                            "@media (min-width: 1280px)":
                                                                {
                                                                    fontSize:
                                                                        "0.75rem",
                                                                    padding:
                                                                        "0.375rem 0.5rem !important",
                                                                },
                                                            "@media (min-width: 1536px)":
                                                                {
                                                                    fontSize:
                                                                        "0.875rem",
                                                                    padding:
                                                                        "0.5rem 0.75rem !important",
                                                                },
                                                            "@media (min-width: 1920px)":
                                                                {
                                                                    fontSize:
                                                                        "1rem",
                                                                    padding:
                                                                        "0.625rem 1rem !important",
                                                                },
                                                        },
                                                }}
                                            />
                                        )}
                                        renderOption={(
                                            props,
                                            option,
                                            { selected }
                                        ) => (
                                            <MenuItem
                                                {...props}
                                                className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                                sx={{
                                                    transition:
                                                        "background-color 0.1s ease",
                                                    padding: "2px 8px",
                                                    minHeight: "32px",
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selected}
                                                    className="xl:p-0.5 2xl:p-1 3xl:p-1.5"
                                                    sx={{
                                                        color: "rgb(239, 68, 68)",
                                                        padding: "4px",
                                                        "&.Mui-checked": {
                                                            color: "rgb(239, 68, 68)",
                                                        },
                                                        "& .MuiSvgIcon-root": {
                                                            transition:
                                                                "transform 0.1s ease",
                                                            fontSize: "18px",
                                                        },
                                                    }}
                                                />
                                                <ListItemText
                                                    primary={`${option.sub_category_name} - ${option.category_name}`}
                                                    className="xl:ml-1 2xl:ml-2 3xl:ml-3"
                                                    primaryTypographyProps={{
                                                        className:
                                                            "xl:text-xs 2xl:text-sm 3xl:text-base",
                                                    }}
                                                />
                                            </MenuItem>
                                        )}
                                        renderTags={(tagValue, getTagProps) =>
                                            tagValue.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option.id}
                                                    label={
                                                        option.sub_category_name
                                                    }
                                                    className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                                    sx={{
                                                        backgroundColor:
                                                            "rgb(254, 226, 226)",
                                                        color: "black",
                                                        height: "24px",
                                                        margin: "1px !important",
                                                        "& .MuiChip-label": {
                                                            padding: "0 6px",
                                                        },
                                                        "& .MuiChip-deleteIcon":
                                                            {
                                                                color: "rgb(239, 68, 68)",
                                                                "&:hover": {
                                                                    color: "rgb(220, 38, 38)",
                                                                },
                                                            },
                                                    }}
                                                />
                                            ))
                                        }
                                    />
                                    {fieldState.error && (
                                        <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                            {fieldState.error.message}
                                        </span>
                                    )}
                                </div>
                            )}
                        />
                    </div>

                    <Controller
                        name="region"
                        control={control}
                        rules={{ required: "Region is required" }}
                        render={({ field, fieldState }) => (
                            <FormField
                                label="Region"
                                {...field}
                                required
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />

                    <div className="space-y-2">
                        <label className="block xl:text-xs 2xl:text-sm 3xl:text-base font-medium text-gray-700">
                            Sectors
                        </label>
                        <Controller
                            name="sectors"
                            control={control}
                            render={({ field, fieldState }) => (
                                <div className="relative">
                                    <Autocomplete
                                        multiple
                                        disableCloseOnSelect
                                        options={sectors?.data || []}
                                        getOptionLabel={(option) => option.name}
                                        value={
                                            (sectors?.data || []).filter(
                                                (sector) =>
                                                    field.value?.includes(
                                                        sector.id
                                                    )
                                            ) || []
                                        }
                                        onChange={(_, newValue) => {
                                            field.onChange(
                                                newValue.map((item) => item.id)
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                className="w-full xl:text-xs 2xl:text-sm 3xl:text-base"
                                                sx={{
                                                    "& .MuiOutlinedInput-root":
                                                        {
                                                            padding:
                                                                "0px 8px !important",
                                                            "& .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    border: "none",
                                                                },
                                                        },
                                                    "& .MuiInputBase-root": {
                                                        border: "1px solid rgb(229, 231, 235)",
                                                        borderRadius:
                                                            "0.375rem",
                                                        "@media (min-width: 1280px)":
                                                            {
                                                                borderWidth:
                                                                    "1px",
                                                                borderRadius:
                                                                    "0.375rem",
                                                            },
                                                        "@media (min-width: 1536px)":
                                                            {
                                                                borderWidth:
                                                                    "2px",
                                                                borderRadius:
                                                                    "0.5rem",
                                                            },
                                                        "@media (min-width: 1920px)":
                                                            {
                                                                borderWidth:
                                                                    "2px",
                                                                borderRadius:
                                                                    "0.5rem",
                                                            },
                                                        "&:hover": {
                                                            borderColor:
                                                                "rgb(239, 68, 68)",
                                                        },
                                                        "&.Mui-focused": {
                                                            borderColor:
                                                                "rgb(239, 68, 68)",
                                                            boxShadow:
                                                                "0 0 0 2px rgba(239, 68, 68, 0.2)",
                                                        },
                                                    },
                                                    "& .MuiAutocomplete-input":
                                                        {
                                                            padding:
                                                                "0.375rem 0.5rem !important",
                                                            "@media (min-width: 1280px)":
                                                                {
                                                                    fontSize:
                                                                        "0.75rem",
                                                                    padding:
                                                                        "0.375rem 0.5rem !important",
                                                                },
                                                            "@media (min-width: 1536px)":
                                                                {
                                                                    fontSize:
                                                                        "0.875rem",
                                                                    padding:
                                                                        "0.5rem 0.75rem !important",
                                                                },
                                                            "@media (min-width: 1920px)":
                                                                {
                                                                    fontSize:
                                                                        "1rem",
                                                                    padding:
                                                                        "0.625rem 1rem !important",
                                                                },
                                                        },
                                                }}
                                            />
                                        )}
                                        renderOption={(
                                            props,
                                            option,
                                            { selected }
                                        ) => (
                                            <MenuItem
                                                {...props}
                                                className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                                sx={{
                                                    transition:
                                                        "background-color 0.1s ease",
                                                    padding: "2px 8px",
                                                    minHeight: "32px",
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selected}
                                                    className="xl:p-0.5 2xl:p-1 3xl:p-1.5"
                                                    sx={{
                                                        color: "rgb(239, 68, 68)",
                                                        padding: "4px",
                                                        "&.Mui-checked": {
                                                            color: "rgb(239, 68, 68)",
                                                        },
                                                        "& .MuiSvgIcon-root": {
                                                            transition:
                                                                "transform 0.1s ease",
                                                            fontSize: "18px",
                                                        },
                                                    }}
                                                />
                                                <ListItemText
                                                    primary={option.name}
                                                    className="xl:ml-1 2xl:ml-2 3xl:ml-3"
                                                    primaryTypographyProps={{
                                                        className:
                                                            "xl:text-xs 2xl:text-sm 3xl:text-base",
                                                    }}
                                                />
                                            </MenuItem>
                                        )}
                                        renderTags={(tagValue, getTagProps) =>
                                            tagValue.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option.id}
                                                    label={option.name}
                                                    className="xl:text-xs 2xl:text-sm 3xl:text-base"
                                                    sx={{
                                                        backgroundColor:
                                                            "rgb(254, 226, 226)",
                                                        color: "black",
                                                        height: "24px",
                                                        margin: "1px !important",
                                                        "& .MuiChip-label": {
                                                            padding: "0 6px",
                                                        },
                                                        "& .MuiChip-deleteIcon":
                                                            {
                                                                color: "rgb(239, 68, 68)",
                                                                "&:hover": {
                                                                    color: "rgb(220, 38, 38)",
                                                                },
                                                            },
                                                    }}
                                                />
                                            ))
                                        }
                                    />
                                    {fieldState.error && (
                                        <span className="block xl:text-xs 2xl:text-sm 3xl:text-base text-red-500 mt-1">
                                            {fieldState.error.message}
                                        </span>
                                    )}
                                </div>
                            )}
                        />
                    </div>

                    <ActionButtons
                        onClose={handleClose}
                        ctaText={ctaText}
                        onSave={handleSubmit(onSubmit)}
                        isLoading={isLoading || isCreating}
                    />
                </div>
            </div>
        </CommonDialog>
    );
};

ClientDetailDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    rowData: PropTypes.object,
    isEdit: PropTypes.bool,
    title: PropTypes.string,
    ctaText: PropTypes.string,
};

export default ClientDetailDialog;
